import React, { useCallback, useEffect, useRef, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../../store";
import WritingPreviewToolbar from "./WritingPreviewToolbar";
import WritingPreviewPane from "./WritingPreviewPane";
import WritingPreviewChangesSidebar from "./WritingPreviewChangesSidebar";
import { useWritingJobValidations } from "./useWritingJobValidations";
import { useWritingPreviewEdits } from "./useWritingPreviewEdits";
import {
	deployAllFromWriting,
	getWritingDeployApi,
} from "../../../../services/writingDeployApi";
import {
	hasEligibleChangesForDeployAll,
	isChangeEligibleForAutoDeploy,
	jobHasOpenDeployment,
	OPEN_JOB_LOCK_MESSAGE,
} from "../writingAssistantUtils";
// import WritingPreviewDebugModal from "./WritingPreviewDebugModal";

const SCROLL_SYNC_SUPPRESS_MS = 800;

function getChangeDeployJobId(change) {
	const raw = change?.raw || change || {};
	const id =
		raw.deployment_job_id ??
		raw.deploy_job_id ??
		raw.wordpress_job_id ??
		raw.wp_job_id ??
		raw.job_id ??
		change?.deploymentJobId ??
		change?.deployJobId ??
		null;
	if (id == null || id === "") return null;
	return id;
}

function writingPreviewReturnPath(writingJobId) {
	if (writingJobId == null || writingJobId === "") return null;
	return `/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(
		writingJobId
	)}`;
}

function deployViewPath(deployJobId, writingJobId) {
	const params = new URLSearchParams();
	params.set("jobId", String(deployJobId));
	const returnTo = writingPreviewReturnPath(writingJobId);
	if (returnTo) params.set("returnTo", returnTo);
	return `/aivisibility/deployments/view?${params.toString()}`;
}

export default function WritingPreviewLayout({ job, onPreviewUpdated }) {
	const navigate = useNavigate();
	const { currentWebsiteId, websitePlatform } = useContext(ProductContext);
	const deployApi = useMemo(
		() => getWritingDeployApi(websitePlatform),
		[websitePlatform]
	);
	const [activeChangeIndex, setActiveChangeIndex] = useState(-1);
	const [scrollRequestId, setScrollRequestId] = useState(0);
	const [deployingIndex, setDeployingIndex] = useState(null);
	const [pendingDeployIndex, setPendingDeployIndex] = useState(null);
	const [pendingDeployAll, setPendingDeployAll] = useState(false);
	const [isDeployingAll, setIsDeployingAll] = useState(false);
	// const [debugOpen, setDebugOpen] = useState(false);
	const changeRefs = useRef([]);
	const suppressScrollSyncRef = useRef(false);

	const changes = job?.preview?.changes ?? [];
	const skipped = job?.preview?.skipped ?? [];
	const debugRecommendations = job?.preview?.pipeline_debug?.recommendations ?? [];
	const autoDeployment = Boolean(job?.auto_deployment);
	const recommendationId = job?.recommendation_id ?? null;
	const jobId = job?.id;
	const jobStatus = job?.status;
	const deployBusy = deployingIndex != null || isDeployingAll;

	const {
		validations,
		latestValidation,
		isLoadingValidations,
		isSubmitting,
		isPolling,
		validationStatusLabel,
		handleVerify,
	} = useWritingJobValidations(currentWebsiteId, jobId, changes.length);

	const {
		getDraftValue,
		setDraft,
		clearDraft,
		isDirtyAtIndex,
		isIndexSaved,
		getInlineRejection,
		isDirty,
		isSaving,
		savingIndex,
		submitSingleEdit,
	} = useWritingPreviewEdits(
		currentWebsiteId,
		jobId,
		jobStatus,
		changes.length,
		changes
	);

	const handleEditToggle = useCallback((index, editing) => {
		// Left intentionally minimal – toggle state lives per-callout. The hook
		// does not need to know about the edit-mode UI state.
		void index;
		void editing;
	}, []);

	const handleDraftChange = useCallback(
		(index, value, fallbackMd) => {
			setDraft(index, value, fallbackMd);
		},
		[setDraft]
	);

	const handleClearDraft = useCallback(
		(index) => {
			clearDraft(index);
		},
		[clearDraft]
	);

	const handleSaveSingleEdit = useCallback(
		async (index) => {
			return submitSingleEdit(index, {
				onPreviewRefreshed: (preview) => {
					if (typeof onPreviewUpdated === "function") {
						onPreviewUpdated(preview);
					}
				},
			});
		},
		[submitSingleEdit, onPreviewUpdated]
	);

	const isSavingThisAtIndex = useCallback(
		(index) => {
			// Guard against Number(null) === Number(0) → 0 === 0 (falsy match on first block index=0)
			// and non-numeric / negative values.
			if (savingIndex === null || savingIndex === undefined) return false;
			const si = Number(savingIndex);
			if (!Number.isFinite(si) || si < 0) return false;
			const ti = Number(index);
			if (!Number.isFinite(ti) || ti < 0) return false;
			return si === ti;
		},
		[savingIndex]
	);

	useEffect(() => {
		changeRefs.current = [];
		setActiveChangeIndex(-1);
		setScrollRequestId(0);
	}, [job?.id, changes.length]);

	const navigateToChange = useCallback((index) => {
		suppressScrollSyncRef.current = true;
		setActiveChangeIndex(index);
		setScrollRequestId((id) => id + 1);
		window.setTimeout(() => {
			suppressScrollSyncRef.current = false;
		}, SCROLL_SYNC_SUPPRESS_MS);
	}, []);

	const handleJumpToEdits = useCallback(() => {
		if (changes.length === 0) return;
		const current = activeChangeIndex < 0 ? -1 : activeChangeIndex;
		const next = (current + 1) % changes.length;
		navigateToChange(next);
	}, [activeChangeIndex, changes.length, navigateToChange]);

	const handleChangeVisible = useCallback((index) => {
		setActiveChangeIndex((prev) => (prev === index ? prev : index));
	}, []);

	const closeDeployConfirm = useCallback(() => {
		if (deployBusy) return;
		setPendingDeployIndex(null);
		setPendingDeployAll(false);
	}, [deployBusy]);

	const requestDeployChange = useCallback(
		(changeIndex) => {
			if (!currentWebsiteId || !job?.id || deployBusy) return;
			if (jobHasOpenDeployment(changes)) {
				toast.info(OPEN_JOB_LOCK_MESSAGE);
				return;
			}
			const change = changes[changeIndex];
			if (!change || !isChangeEligibleForAutoDeploy(change)) return;
			setPendingDeployAll(false);
			setPendingDeployIndex(changeIndex);
		},
		[currentWebsiteId, job?.id, deployBusy, changes]
	);

	const requestDeployAll = useCallback(() => {
		if (
			!currentWebsiteId ||
			!job?.id ||
			recommendationId == null ||
			deployBusy ||
			!autoDeployment ||
			!hasEligibleChangesForDeployAll(changes)
		) {
			return;
		}
		if (jobHasOpenDeployment(changes)) {
			toast.info(OPEN_JOB_LOCK_MESSAGE);
			return;
		}
		setPendingDeployIndex(null);
		setPendingDeployAll(true);
	}, [
		currentWebsiteId,
		job?.id,
		recommendationId,
		deployBusy,
		autoDeployment,
		changes,
	]);

	const confirmDeployChange = useCallback(async () => {
		const changeIndex = pendingDeployIndex;
		if (
			changeIndex == null ||
			!currentWebsiteId ||
			!job?.id ||
			deployBusy
		) {
			return;
		}
		if (jobHasOpenDeployment(changes)) {
			toast.info(OPEN_JOB_LOCK_MESSAGE);
			setPendingDeployIndex(null);
			return;
		}
		setDeployingIndex(changeIndex);
		try {
			const result = await deployApi.deployFromWriting({
				websiteId: currentWebsiteId,
				writingJobId: job.id,
				url: job.url || "",
				changeIndex,
			});
			if (!result.ok || result.jobId == null) {
				toast.error(result.message || "Failed to start deploy");
				return;
			}
			setPendingDeployIndex(null);
			toast.success(result.message || "Deployment started");
			navigate(deployViewPath(result.jobId, job.id));
		} catch {
			toast.error("Failed to start deploy");
		} finally {
			setDeployingIndex(null);
		}
	}, [
		pendingDeployIndex,
		currentWebsiteId,
		job?.id,
		job?.url,
		deployBusy,
		changes,
		navigate,
		deployApi,
	]);

	const confirmDeployAll = useCallback(async () => {
		if (
			!pendingDeployAll ||
			!currentWebsiteId ||
			!job?.id ||
			recommendationId == null ||
			deployBusy ||
			!hasEligibleChangesForDeployAll(changes)
		) {
			return;
		}
		if (jobHasOpenDeployment(changes)) {
			toast.info(OPEN_JOB_LOCK_MESSAGE);
			setPendingDeployAll(false);
			return;
		}
		setIsDeployingAll(true);
		try {
			const changeIndices = changes
				.map((change, index) =>
					isChangeEligibleForAutoDeploy(change) ? index : null
				)
				.filter((index) => index != null);
			const result = await deployAllFromWriting({
				websiteId: currentWebsiteId,
				writingJobId: job.id,
				url: job.url || "",
				recommendationId,
				changeIndices,
				platform: websitePlatform,
			});
			if (!result.ok || result.jobId == null) {
				toast.error(result.message || "Failed to start deploy");
				return;
			}
			setPendingDeployAll(false);
			toast.success(result.message || "Deployment started");
			navigate(deployViewPath(result.jobId, job.id));
		} catch {
			toast.error("Failed to start deploy");
		} finally {
			setIsDeployingAll(false);
		}
	}, [
		pendingDeployAll,
		currentWebsiteId,
		job?.id,
		job?.url,
		recommendationId,
		deployBusy,
		changes,
		navigate,
		websitePlatform,
	]);

	const resolveDeployJobId = useCallback(
		async (changeIndex) => {
			if (!currentWebsiteId || !job?.id || changeIndex == null) return null;
			const change = changes[changeIndex];
			if (!change) return null;

			let deployJobId = getChangeDeployJobId(change);
			if (deployJobId == null) {
				const listed = await deployApi.listDeployJobs(currentWebsiteId);
				if (listed.ok) {
					const match = (listed.jobs || []).find(
						(j) =>
							Number(j.writingJobId) === Number(job.id) &&
							Number(j.changeIndex) === Number(changeIndex)
					);
					deployJobId = match?.id ?? null;
				}
			}
			return deployJobId;
		},
		[currentWebsiteId, job?.id, changes, deployApi]
	);

	const openDeployStatus = useCallback(
		async (changeIndex) => {
			const deployJobId = await resolveDeployJobId(changeIndex);
			if (deployJobId == null) {
				toast.info("No deployment job found for this change yet.");
				return;
			}
			navigate(deployViewPath(deployJobId, job.id));
		},
		[resolveDeployJobId, job?.id, navigate]
	);

	const isConfirmingSingleDeploy =
		deployingIndex != null && pendingDeployIndex != null;
	const isConfirmingDeployAll = isDeployingAll && pendingDeployAll;
	const isConfirmingAny = isConfirmingSingleDeploy || isConfirmingDeployAll;

	return (
		<div className="writing-preview__layout">
			<WritingPreviewToolbar
				changeCount={changes.length}
				validations={validations}
				isSubmitting={isSubmitting}
				isPolling={isPolling}
				isLoadingValidations={isLoadingValidations}
				validationStatusLabel={validationStatusLabel}
				onVerify={handleVerify}
				onJumpToEdits={handleJumpToEdits}
				// onDebugOpen={() => setDebugOpen(true)}

				editingEnabled={true}
				isDirty={isDirty}
				isSaving={isSaving}
			/>
			<div className="writing-preview__columns">
				<WritingPreviewPane
					changes={changes}
					originalMd={job?.preview?.original_md}
					patchedMd={job?.preview?.patched_md}
					activeChangeIndex={activeChangeIndex}
					scrollRequestId={scrollRequestId}
					latestValidation={latestValidation}
					onChangeVisible={handleChangeVisible}
					changeRefs={changeRefs}
					suppressScrollSyncRef={suppressScrollSyncRef}

					editingEnabled={true}
					isSavingAll={isSaving}
					isSavingThisAtIndex={isSavingThisAtIndex}
					getDraftValueForIndex={getDraftValue}
					isDirtyAtIndex={isDirtyAtIndex}
					isIndexSaved={isIndexSaved}
					getInlineRejectionForIndex={getInlineRejection}
					onEditToggleAtIndex={handleEditToggle}
					onDraftChangeAtIndex={handleDraftChange}
					onClearDraftAtIndex={handleClearDraft}
					onSaveSingleAtIndex={handleSaveSingleEdit}
				/>
				<WritingPreviewChangesSidebar
					changes={changes}
					skipped={skipped}
					debugRecommendations={debugRecommendations}
					activeChangeIndex={activeChangeIndex}
					latestValidation={latestValidation}
					onSelectChange={navigateToChange}
					autoDeployment={autoDeployment}
					recommendationId={recommendationId}
					deployingIndex={deployingIndex}
					isDeployingAll={isDeployingAll}
					onDeployChange={requestDeployChange}
					onDeployAll={requestDeployAll}
					onOpenDeployStatus={openDeployStatus}

					hasUnsavedEdits={isDirty}
					isSavingEdits={isSaving}
				/>
			</div>

			<Modal
				isOpen={pendingDeployIndex != null}
				toggle={closeDeployConfirm}
				centered
				className="writing-preview__deploy-confirm-modal"
				backdrop
				keyboard={!isConfirmingAny}
			>
				<ModalHeader toggle={isConfirmingAny ? undefined : closeDeployConfirm}>
					Start deploy?
				</ModalHeader>
				<ModalBody>
					<p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
						Once deploy starts, this change can’t be edited. You can still Discard it later if you don’t want to overwrite the live page.
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						outline
						onClick={closeDeployConfirm}
						disabled={isConfirmingAny}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						onClick={confirmDeployChange}
						disabled={isConfirmingAny}
					>
						{isConfirmingSingleDeploy ? (
							<>
								<Spinner size="sm" className="me-2" />
								Starting…
							</>
						) : (
							"Start deploy"
						)}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal
				isOpen={pendingDeployAll}
				toggle={closeDeployConfirm}
				centered
				className="writing-preview__deploy-confirm-modal"
				backdrop
				keyboard={!isConfirmingAny}
			>
				<ModalHeader toggle={isConfirmingAny ? undefined : closeDeployConfirm}>
					Deploy all changes?
				</ModalHeader>
				<ModalBody>
					<p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
						This deploys all changes for this recommendation. Once deploy starts, those changes can’t be edited. You can still Discard later if you don’t want to overwrite the live page.
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						outline
						onClick={closeDeployConfirm}
						disabled={isConfirmingAny}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						onClick={confirmDeployAll}
						disabled={isConfirmingAny}
					>
						{isConfirmingDeployAll ? (
							<>
								<Spinner size="sm" className="me-2" />
								Starting…
							</>
						) : (
							"Deploy all"
						)}
					</Button>
				</ModalFooter>
			</Modal>

			{/* <WritingPreviewDebugModal
				isOpen={debugOpen}
				toggle={() => setDebugOpen(false)}
				job={job}
				debugRecommendation={debugRecommendation}
			/> */}
		</div>
	);
}
